import React, { useEffect, useMemo, useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { AddCircleBlue, MinLoader, NoItemsYet, SimpleTooltip } from 'components';
import { goalActions } from 'store';
import { Images, sanitizeHtml, PermissionList, RolePermission } from 'utils';
import { RichTextElement } from '../../../../../../pages/forms/formDetail/formBody/elements';
import { resolveRichTextImages } from '../../../../../../pages/forms/utils/resolveRichTextImages';

export const GoalDetailInfo = ({ requestParams }) => {
    const goalById = useSelector((state) => state.goals.goalById);
    const sectionTypes = useSelector((state) => state.goals.sectionTypes);
    const dispatch = useDispatch();
    const [openSections, setOpenSections] = useState({});
    const [editingSection, setEditingSection] = useState(null);
    const [draftValue, setDraftValue] = useState('');
    const [sectionMenuAnchor, setSectionMenuAnchor] = useState(null);
    const [pendingSections, setPendingSections] = useState({});
    const [savingSection, setSavingSection] = useState(null);
    const [deletingSection, setDeletingSection] = useState(null);
    const [pendingImageUrls, setPendingImageUrls] = useState({});
    const hasPermission = RolePermission([PermissionList.GOAL_CREATE?.code]);

    const imageUrls = useMemo(() => ({ ...pendingImageUrls, ...(goalById?.imageUrls || {}) }), [pendingImageUrls, goalById]);

    /** Fetch available section types */
    useEffect(() => {
        dispatch(goalActions.getSectionTypes());
    }, [dispatch]);

    /** Map sectionType id → name */
    const sectionTypeMap = useMemo(() => {
        const map = {};
        sectionTypes.forEach((st) => {
            map[st.id] = st.name;
        });
        return map;
    }, [sectionTypes]);

    /** Detect save success */
    useEffect(() => {
        if (!savingSection) return;
        const blocks = goalById?.sectionDescriptionBlocks || [];
        const currentBlock = blocks.find((b) => b.sectionType === savingSection?.sectionTypeId);

        const isNewSaveSuccess = !savingSection?.previousExists && !!currentBlock;
        const isExistingSaveSuccess =
            savingSection?.previousExists &&
            currentBlock &&
            (currentBlock?.updatedAt !== savingSection?.previousUpdatedAt || currentBlock?.value !== savingSection?.previousValue);

        if (isNewSaveSuccess || isExistingSaveSuccess) {
            setSavingSection(null);
            setEditingSection(null);
            setDraftValue('');
            setOpenSections((prev) => ({ ...prev, [savingSection.sectionTypeId]: false }));
            setPendingSections((prev) => {
                if (!prev[savingSection.sectionTypeId]) return prev;
                const next = { ...prev };
                delete next[savingSection.sectionTypeId];
                return next;
            });
        }
    }, [goalById, savingSection]);

    /** Detect delete success */
    useEffect(() => {
        if (!deletingSection) return;
        const blocks = goalById?.sectionDescriptionBlocks || [];
        const stillExists = blocks.some((b) => b.sectionType === deletingSection);
        if (!stillExists) {
            setDeletingSection(null);
        }
    }, [goalById, deletingSection]);

    /** Build sections from backend data */
    const sections = useMemo(() => {
        const blocks = goalById?.sectionDescriptionBlocks || [];
        return blocks.map((block) => ({
            sectionTypeId: block.sectionType,
            value: block.value,
            createdAt: block.createdAt,
            updatedAt: block.updatedAt,
        }));
    }, [goalById]);

    /** Auto-open sections */
    useEffect(() => {
        if (!sections.length) return;
        setOpenSections((prev) => {
            const next = { ...prev };
            sections.forEach((section) => {
                if (next[section.sectionTypeId] === undefined) {
                    next[section.sectionTypeId] = true;
                }
            });
            return next;
        });
    }, [sections]);

    /** Merge backend sections + pending (unsaved) sections */
    const displaySections = useMemo(() => {
        const pendingOnly = Object.keys(pendingSections)
            .filter((stId) => !sections.some((section) => section.sectionTypeId === stId))
            .map((stId) => ({
                sectionTypeId: stId,
                ...pendingSections[stId],
                isPending: true,
            }));
        return [...sections, ...pendingOnly];
    }, [sections, pendingSections]);

    const isPending = displaySections.some((section) => section.isPending);

    /** Available section types to add (exclude already used) */
    const existingSectionTypeIds = [...sections.map((s) => s.sectionTypeId), ...Object.keys(pendingSections)];
    const sectionOptions = sectionTypes.filter((st) => !existingSectionTypeIds.includes(st.id));

    const handleToggleSection = (sectionTypeId) => {
        setOpenSections((prev) => ({
            ...prev,
            [sectionTypeId]: !prev[sectionTypeId],
        }));
    };

    const handleEdit = (sectionTypeId, value) => {
        setOpenSections((prev) => ({ ...prev, [sectionTypeId]: true }));
        setEditingSection(sectionTypeId);
        setDraftValue(value || '');
    };

    const handleCancelEdit = () => {
        if (editingSection && pendingSections[editingSection]) {
            setOpenSections((prev) => ({ ...prev, [editingSection]: false }));
            setPendingSections((prev) => {
                const next = { ...prev };
                delete next[editingSection];
                return next;
            });
        }
        setEditingSection(null);
        setDraftValue('');
    };

    const handleSave = (sectionTypeId) => {
        if (!goalById?.id) return;
        const blocks = goalById?.sectionDescriptionBlocks || [];
        const previousBlock = blocks.find((b) => b.sectionType === sectionTypeId);
        setSavingSection({
            sectionTypeId,
            previousExists: !!previousBlock,
            previousUpdatedAt: previousBlock?.updatedAt || null,
            previousValue: previousBlock?.value || '',
        });
        dispatch(
            goalActions.upsertSectionDescriptionBlock(
                goalById.id,
                {
                    sectionType: sectionTypeId,
                    value: draftValue || '',
                },
                requestParams
            )
        );
    };

    const handleAddSection = (sectionType) => {
        if (savingSection) return;
        setSectionMenuAnchor(null);
        const now = new Date().toISOString();
        setPendingSections((prev) => ({
            ...prev,
            [sectionType.id]: {
                value: '',
                createdAt: now,
                updatedAt: now,
            },
        }));
        setOpenSections((prev) => ({ ...prev, [sectionType.id]: true }));
        setEditingSection(sectionType.id);
        setDraftValue('');
    };

    const handleDraftChange = (value) => {
        setDraftValue(value);
        if (editingSection && pendingSections[editingSection]) {
            setPendingSections((prev) => ({
                ...prev,
                [editingSection]: {
                    ...prev[editingSection],
                    value,
                    updatedAt: new Date().toISOString(),
                },
            }));
        }
    };

    const handleDeleteSection = (sectionTypeId) => {
        if (pendingSections[sectionTypeId]) {
            setPendingSections((prev) => {
                const next = { ...prev };
                delete next[sectionTypeId];
                return next;
            });
            setOpenSections((prev) => ({ ...prev, [sectionTypeId]: false }));
            if (editingSection === sectionTypeId) setEditingSection(null);
            setDraftValue('');
            return;
        }
        if (!goalById?.id) return;
        setDeletingSection(sectionTypeId);
        dispatch(goalActions.deleteSectionDescriptionBlock(goalById.id, sectionTypeId, requestParams));
        if (editingSection === sectionTypeId) {
            handleCancelEdit();
        }
    };

    const getSectionName = (sectionTypeId) => {
        return sectionTypeMap[sectionTypeId] || sectionTypeId;
    };

    /** A save or delete re-fetches the goal, which would race a reorder's indexes */
    const isReorderDisabled = !hasPermission || !!savingSection || !!deletingSection;

    const handleDragEnd = ({ source, destination }) => {
        if (!destination || source.index === destination.index || !goalById?.id) return;
        const blocks = Array.from(goalById.sectionDescriptionBlocks || []);
        const [moved] = blocks.splice(source.index, 1);
        blocks.splice(destination.index, 0, moved);
        dispatch(
            goalActions.reorderSectionDescriptionBlocks(
                { ...goalById, sectionDescriptionBlocks: blocks },
                { indexFrom: source.index, indexTo: destination.index }
            )
        );
    };

    /** dragProvided is absent for pending (unsaved) sections — they have no server-side position yet */
    const renderSection = (section, dragProvided, isDragging) => {
        const isOpen = !!openSections[section.sectionTypeId];
        const isEditing = editingSection === section.sectionTypeId;
        const createdAt = section?.createdAt || goalById?.createdAt;
        const updatedAt = section?.updatedAt || goalById?.updatedAt;

        return (
            <div
                className="goal-detail-description-section"
                key={section.sectionTypeId}
                ref={dragProvided?.innerRef}
                {...dragProvided?.draggableProps}
                style={{
                    ...dragProvided?.draggableProps?.style,
                    ...(isDragging && { borderColor: '#0C66E3', boxShadow: '0 4px 12px rgba(16, 24, 40, 0.12)' }),
                }}
            >
                <div onClick={() => handleToggleSection(section.sectionTypeId)} className="goal-detail-description-section-header">
                    <div className="goal-detail-description-section-title-box">
                        {dragProvided && hasPermission && (
                            <div
                                className="goal-detail-drag-handle"
                                {...dragProvided.dragHandleProps}
                                onClick={(e) => e.stopPropagation()}
                                title="Drag to reorder"
                            >
                                <img src={Images.dragIndicator} alt="Drag to reorder" />
                            </div>
                        )}
                        <p className="goal-detail-description-section-title">{getSectionName(section.sectionTypeId)}</p>
                        {!section.isPending && (
                            <>
                                <p className="goal-detail-description-date">
                                    Created: {createdAt ? moment(createdAt).format('MM/DD/YYYY') : '--'}
                                </p>
                                <div className="goal-detail-date-divider" />
                                <p className="goal-detail-description-date">
                                    Last Modified: {updatedAt ? moment(updatedAt).format('MM/DD/YYYY') : '--'}
                                </p>
                            </>
                        )}
                    </div>
                    <div className="goal-detail-description-actions">
                        {!isEditing && hasPermission && (
                            <button
                                type="button"
                                className="goal-detail-action-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEdit(section.sectionTypeId, section?.value);
                                }}
                            >
                                <img src={Images.edit} alt="" />
                            </button>
                        )}
                        {hasPermission && (
                            <button
                                type="button"
                                className="goal-detail-action-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteSection(section.sectionTypeId);
                                }}
                                disabled={deletingSection === section.sectionTypeId}
                            >
                                {deletingSection === section.sectionTypeId ? (
                                    <MinLoader color="#667085" position="relative" margin="0" width="16px" height="16px" />
                                ) : (
                                    <img src={Images.trash} alt="" />
                                )}
                            </button>
                        )}
                        <div className="goal-detail-action-divider" />
                        <button
                            type="button"
                            className="goal-detail-chevron-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleToggleSection(section.sectionTypeId);
                            }}
                        >
                            <img src={isOpen ? Images.dropUp : Images.dropDown} alt="" />
                        </button>
                    </div>
                </div>

                {isOpen && (
                    <div className="goal-detail-description-section-body">
                        {isEditing ? (
                            <div>
                                <RichTextElement
                                    value={draftValue}
                                    onChange={handleDraftChange}
                                    noMentions
                                    imageUrls={imageUrls}
                                    onImageUpload={(key, url) => setPendingImageUrls((prev) => ({ ...prev, [key]: url }))}
                                />
                                <div className="goal-detail-description-editor-actions">
                                    <button
                                        type="button"
                                        className="goal-detail-cancel-btn"
                                        onClick={handleCancelEdit}
                                        disabled={savingSection?.sectionTypeId === section.sectionTypeId}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="goal-detail-done-btn"
                                        onClick={() => handleSave(section.sectionTypeId)}
                                        disabled={savingSection?.sectionTypeId === section.sectionTypeId}
                                    >
                                        {savingSection?.sectionTypeId === section.sectionTypeId ? (
                                            <MinLoader color="#FFFFFF" position="relative" margin="0" width="18px" height="18px" />
                                        ) : (
                                            'Done'
                                        )}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <p
                                className="richtext-readonly-content"
                                dangerouslySetInnerHTML={{
                                    __html: sanitizeHtml(resolveRichTextImages(section?.value, imageUrls)) || '--',
                                }}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="goal-details-form-wrapper">
            {hasPermission && (
                <div className="goal-detail-description-header">
                    <AddCircleBlue
                        styles={{ opacity: isPending ? 0.5 : 1 }}
                        handleClick={(event) => setSectionMenuAnchor(event.currentTarget)}
                        disabled={isPending || !sectionOptions.length || !!savingSection}
                        text={'Add Section'}
                    />
                    <Menu
                        anchorEl={sectionMenuAnchor}
                        open={Boolean(sectionMenuAnchor)}
                        onClose={() => setSectionMenuAnchor(null)}
                        keepMounted
                    >
                        {sectionOptions?.map((st) => (
                            <MenuItem key={st.id} onClick={() => handleAddSection(st)}>
                                {st.name}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>
            )}

            <div className="goal-detail-description-list">
                {!displaySections.length && (
                    <NoItemsYet
                        text={'No goal body details added yet'}
                        subTitle={'Add a section to provide clinical documentation.'}
                        image={Images.noClientYet}
                    />
                )}

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="goalSectionDescriptionBlocks">
                        {(dropProvided) => (
                            <div className="goal-detail-description-droppable" ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                                {sections.map((section, index) => (
                                    <Draggable
                                        key={section.sectionTypeId}
                                        draggableId={section.sectionTypeId}
                                        index={index}
                                        isDragDisabled={isReorderDisabled}
                                    >
                                        {(dragProvided, snapshot) => renderSection(section, dragProvided, snapshot.isDragging)}
                                    </Draggable>
                                ))}
                                {dropProvided.placeholder}
                                {displaySections.filter((section) => section.isPending).map((section) => renderSection(section))}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
        </div>
    );
};
